/**
 * Session Config screen (03) — per PRD V1 §7.
 *
 * First vertical slice scope: difficulty presets + core settings (Pot Type,
 * Position, Archetype), Hand Count, Timer, Preflop Animation, Session Focus.
 * Deferred: Flop/Turn/River type conditionals, Blinds + Stack Depth inputs.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionConfig } from '@/hooks/useSessionConfig';
import { sessionManager } from '@/services/sessionManager';
import { recordingPipeline } from '@/services/recordingPipeline';
import { whisperAdapter } from '@/services/whisperAdapter';
import { MicPermissionModal } from '@/components/MicPermissionModal';
import { WhisperLoadingModal } from '@/components/WhisperLoadingModal';
import { WhisperProgressBar } from '@/components/WhisperProgressBar';
import {
  generateAutoName,
  type Archetype,
  type DifficultyPreset,
  type PotType,
  type PreflopAnimation,
  type Position,
} from '@/types/session';
import { cn } from '@/lib/utils';

const POT_TYPES: PotType[] = ['SRP', '3BP', 'Any'];
const POSITIONS: Position[] = ['IP', 'OOP', 'Any'];
const ARCHETYPES: Archetype[] = ['Fish', 'Whale', 'TRec', 'Any'];
const PREFLOP_ANIMATIONS: PreflopAnimation[] = ['Watch', 'Skip'];
const PRESETS: Exclude<DifficultyPreset, 'Custom'>[] = [
  'Beginner',
  'Intermediate',
  'Advanced',
  'Expert',
];
const TIMER_OPTIONS = [60, 45, 30, 15];
const HANDS_COUNT_OPTIONS = [5, 8, 10, 15, 20];

export function SessionConfigScreen() {
  const navigate = useNavigate();
  const { config, activePreset, setField, applyPreset } = useSessionConfig();

  const autoName = generateAutoName(config);

  // Session-start gating: two prerequisites land in sequence —
  //   1. Mic permission (OS prompt). If denied → MicPermissionModal.
  //   2. Whisper model ready. If still loading → WhisperLoadingModal.
  // Only once both are satisfied does startSession + navigate fire.
  const [micDeniedOpen, setMicDeniedOpen] = useState(false);
  const [micRequesting, setMicRequesting] = useState(false);
  const [whisperGateOpen, setWhisperGateOpen] = useState(false);

  const beginSession = () => {
    sessionManager.startSession(config);
    navigate('/play');
  };

  const requestMicAndStart = async () => {
    setMicRequesting(true);
    const result = await recordingPipeline.requestPermission();
    setMicRequesting(false);
    if (result !== 'granted') {
      setMicDeniedOpen(true);
      return;
    }
    setMicDeniedOpen(false);

    // Mic is good — now check the model.
    if (whisperAdapter.isReady()) {
      beginSession();
    } else {
      // Show the loading modal; it subscribes to status and calls
      // onReady when the model finishes loading. The user can also opt
      // to continue without transcription.
      setWhisperGateOpen(true);
    }
  };

  const handleStart = () => {
    void requestMicAndStart();
  };

  return (
    <main className="min-h-dvh bg-surface-app text-text-primary">
      {/* pb-24 reserves room for the fixed WhisperProgressBar so the
          "Start session" CTA never sits under it when the bar is showing. */}
      <div className="mx-auto max-w-2xl px-4 py-8 pb-24 sm:py-12 sm:pb-24">
        <header className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">New session</h1>
          <p className="mt-1 text-sm text-text-secondary">{autoName}</p>
        </header>

        {/* Difficulty presets */}
        <Section title="Difficulty preset">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {PRESETS.map((preset) => (
              <PresetButton
                key={preset}
                label={preset}
                isActive={activePreset === preset}
                onClick={() => applyPreset(preset)}
              />
            ))}
          </div>
          {activePreset === 'Custom' && (
            <p className="mt-2 text-xs text-text-muted">
              Custom — preset settings adjusted individually
            </p>
          )}
        </Section>

        {/* Core scenario settings */}
        <Section title="Scenario">
          <Field label="Pot Type">
            <SegmentedControl
              options={POT_TYPES}
              value={config.potType}
              onChange={(v) => setField('potType', v)}
            />
          </Field>
          <Field label="Position">
            <SegmentedControl
              options={POSITIONS}
              value={config.position}
              onChange={(v) => setField('position', v)}
            />
          </Field>
          <Field label="Opponent Archetype">
            <SegmentedControl
              options={ARCHETYPES}
              value={config.archetype}
              onChange={(v) => setField('archetype', v)}
            />
          </Field>
        </Section>

        {/* Session shape */}
        <Section title="Session">
          <Field label="Number of hands">
            <SegmentedControl
              options={HANDS_COUNT_OPTIONS}
              value={config.handsCount}
              onChange={(v) => setField('handsCount', v)}
            />
          </Field>
          <Field label="Timer (seconds)">
            <SegmentedControl
              options={TIMER_OPTIONS}
              value={config.timerSeconds ?? 30}
              onChange={(v) => setField('timerSeconds', v)}
            />
          </Field>
          <Field label="Preflop animation">
            <SegmentedControl
              options={PREFLOP_ANIMATIONS}
              value={config.preflopAnimation}
              onChange={(v) => setField('preflopAnimation', v)}
            />
          </Field>
        </Section>

        {/* Optional session focus */}
        <Section title="Session focus (optional)">
          <textarea
            value={config.sessionFocus ?? ''}
            onChange={(e) =>
              setField('sessionFocus', e.target.value.trim() || null)
            }
            placeholder="e.g. Slow down on river check decisions"
            rows={2}
            className={cn(
              'w-full resize-none rounded-md border bg-surface-elevated px-3 py-2',
              'text-sm text-text-primary placeholder:text-text-muted',
              'focus:outline-none focus:ring-2 focus:ring-border-focus',
            )}
          />
          <p className="mt-1 text-xs text-text-muted">
            Shapes coaching feedback — does not affect your score.
          </p>
        </Section>

        {/* CTA */}
        <button
          type="button"
          onClick={handleStart}
          className={cn(
            'mt-8 w-full rounded-md bg-brand-primary px-4 py-3 text-base font-semibold',
            'text-text-on-brand transition-colors',
            'hover:bg-brand-primary-hover active:bg-brand-primary-active',
            'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-2',
          )}
        >
          Start session
        </button>
      </div>

      {micDeniedOpen && (
        <MicPermissionModal
          busy={micRequesting}
          onRetry={() => void requestMicAndStart()}
          onCancel={() => setMicDeniedOpen(false)}
        />
      )}

      {whisperGateOpen && (
        <WhisperLoadingModal
          onReady={() => {
            setWhisperGateOpen(false);
            beginSession();
          }}
          onContinueWithoutTranscription={() => {
            setWhisperGateOpen(false);
            beginSession();
          }}
        />
      )}

      {/* Inline progress while the Whisper model downloads in the
          background. Auto-hides once loaded. Non-blocking — the user can
          continue configuring. */}
      <WhisperProgressBar />
    </main>
  );
}

/** Section wrapper. */
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-secondary">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/** Field row with label. */
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-text-secondary">
        {label}
      </label>
      {children}
    </div>
  );
}

/** Segmented control — generic pill selector for a small enumeration. */
function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
}: {
  options: readonly T[];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <div className="grid auto-cols-fr grid-flow-col gap-1 rounded-md border border-border bg-surface-elevated p-1">
      {options.map((opt) => {
        const isActive = opt === value;
        return (
          <button
            key={String(opt)}
            type="button"
            onClick={() => onChange(opt)}
            className={cn(
              'rounded px-3 py-1.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand-primary text-text-on-brand'
                : 'text-text-secondary hover:bg-surface-felt hover:text-text-primary',
            )}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/** Preset button — distinct from segmented control because presets apply multi-field changes. */
function PresetButton({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'border-brand-primary bg-brand-primary text-text-on-brand'
          : 'border-border bg-surface-elevated text-text-secondary hover:border-border-strong hover:text-text-primary',
      )}
    >
      {label}
    </button>
  );
}
